import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Tooltip } from "@mui/material";
import IosSwitch from "components/shared/IosSwitch";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchDocoonConfig } from "store/integrationsStore";
import { useAppDispatch, useAppSelector } from "store/store";

export interface DocoonSendOptionsValue {
    signatureTemplate: string;
    serviceEdc: string;
    typeDocEdc: string;
    signSequential: boolean;
    comment: string;
}

/** `signSequential` comes from the company setting `docoon_sign_sequential_default`. */
export const getDefaultDocoonSendOptions = (signSequential: boolean): DocoonSendOptionsValue => ({
    signatureTemplate: "",
    serviceEdc: "",
    typeDocEdc: "",
    signSequential,
    comment: "",
});

interface DocoonSendOptionsFieldsProps {
    value: DocoonSendOptionsValue;
    onChange: (next: DocoonSendOptionsValue) => void;
    // Docoon requires sequential signing as soon as an informed person is on the contract
    forceSequential?: boolean;
}

const DocoonSendOptionsFields = ({ value, onChange, forceSequential }: DocoonSendOptionsFieldsProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const docoonConfig = useAppSelector((state) => state.integrations.docoonConfig);

    useEffect(() => {
        dispatch(fetchDocoonConfig());
    }, [dispatch]);

    const services = docoonConfig.filter((item) => item.type === "service");
    const docTypes = docoonConfig.filter((item) => item.type === "document_type");
    const templates = docoonConfig.filter((item) => item.type === "template");

    const set = <K extends keyof DocoonSendOptionsValue>(key: K, val: DocoonSendOptionsValue[K]) =>
        onChange({ ...value, [key]: val });

    const selectClass =
        "w-full font-[inherit] text-sm text-gray-900 bg-white border border-[#d8dae0] rounded-lg py-[11px] pl-3 pr-10 cursor-pointer appearance-none transition-colors hover:border-[#c3c6cd] focus:outline-none focus:border-gray-400 focus:shadow-[0_0_0_3px_rgba(156,163,175,0.18)]";

    const chevron = (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        >
            <path
                d="M4 6.5L8 10.5L12 6.5"
                stroke="#6b7280"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    return (
        <div className="flex flex-col gap-5">
            <p className="flex items-center gap-1.5 text-[13px] text-gray-500">
                <SettingsOutlinedIcon sx={{ fontSize: 16 }} className="text-gray-500" />
                <span>
                    {t("DocoonSignModal.ConfigurableHintText")}{" "}
                    <span
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate("/settings/docoon")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate("/settings/docoon");
                            }
                        }}
                        className="cursor-pointer underline underline-offset-2 hover:text-gray-800 focus:outline-none"
                    >
                        {t("DocoonSignModal.ConfigurableHintLink")}
                    </span>
                    .
                </span>
            </p>

            <div className="flex flex-col gap-2">
                <label htmlFor="service-edc" className="text-sm font-medium text-gray-900">
                    {t("DocoonSignModal.OptionsServiceLabel")}
                </label>
                <div className="relative flex">
                    <select
                        id="service-edc"
                        value={value.serviceEdc}
                        onChange={(e) => set("serviceEdc", e.target.value)}
                        className={selectClass}
                    >
                        <option value="">{t("DocoonSignModal.OptionsServiceNone")}</option>
                        {services.map((s) => (
                            <option key={s.id} value={s.value}>
                                {s.value}
                            </option>
                        ))}
                    </select>
                    {chevron}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor="type-doc-edc" className="text-sm font-medium text-gray-900">
                    {t("DocoonSignModal.OptionsDocTypeLabel")}
                </label>
                <div className="relative flex">
                    <select
                        id="type-doc-edc"
                        value={value.typeDocEdc}
                        onChange={(e) => set("typeDocEdc", e.target.value)}
                        className={selectClass}
                    >
                        <option value="">{t("DocoonSignModal.OptionsDocTypeNone")}</option>
                        {docTypes.map((d) => (
                            <option key={d.id} value={d.value}>
                                {d.value}
                            </option>
                        ))}
                    </select>
                    {chevron}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor="sig-template" className="text-sm font-medium text-gray-900">
                    {t("DocoonSignModal.OptionsTemplateLabel")}
                </label>
                <div className="relative flex">
                    <select
                        id="sig-template"
                        value={value.signatureTemplate}
                        onChange={(e) => set("signatureTemplate", e.target.value)}
                        className={selectClass}
                    >
                        <option value="">{t("DocoonSignModal.OptionsTemplateNone")}</option>
                        {templates.map((tpl) => (
                            <option key={tpl.id} value={tpl.value}>
                                {tpl.value}
                            </option>
                        ))}
                    </select>
                    {chevron}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor="docoon-comment" className="text-sm font-medium text-gray-900">
                    {t("DocoonSignModal.OptionsCommentLabel")}
                </label>
                <textarea
                    id="docoon-comment"
                    value={value.comment}
                    onChange={(e) => set("comment", e.target.value)}
                    rows={3}
                    className="w-full font-[inherit] text-sm text-gray-900 bg-white border border-[#d8dae0] rounded-lg py-[11px] px-3 resize-none transition-colors hover:border-[#c3c6cd] focus:outline-none focus:border-gray-400 focus:shadow-[0_0_0_3px_rgba(156,163,175,0.18)]"
                    placeholder={t("DocoonSignModal.OptionsCommentPlaceholder")}
                />
            </div>

            <Tooltip
                title={forceSequential ? t("DocoonSignModal.OptionsSequentialForcedInformed") : ""}
                arrow
                placement="top"
            >
                <div className="flex items-center gap-4 bg-[#fafafa] border border-[#ececf0] rounded-[10px] px-4 py-[14px]">
                    <div className="flex-1 flex flex-col gap-[3px]">
                        <div className="text-sm font-medium text-gray-900">
                            {t("DocoonSignModal.OptionsSequentialLabel")}
                        </div>
                        <div className="text-[13px] text-gray-500 leading-[1.45]">
                            {t("DocoonSignModal.OptionsSequentialDescription")}
                        </div>
                    </div>
                    <IosSwitch
                        checked={value.signSequential || !!forceSequential}
                        disabled={forceSequential}
                        onChange={(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                            set("signSequential", checked)
                        }
                    />
                </div>
            </Tooltip>
        </div>
    );
};

export default DocoonSendOptionsFields;

/**
 * Converts the wizard/modal form state into the API payload shape. `forceSequential` wins over the
 * user's choice without overwriting it, so removing the informed people restores that choice.
 */
export const toDocoonSendOptionsPayload = (v: DocoonSendOptionsValue, forceSequential?: boolean) => ({
    signatureTemplate: v.signatureTemplate || undefined,
    signSequential: v.signSequential || forceSequential ? 1 : 0,
    serviceEdc: v.serviceEdc || undefined,
    typeDocEdc: v.typeDocEdc || undefined,
    comment: v.comment || undefined,
});

/** Basic validation for the two required fields (mirrors the send-modal Confirm button rule). */
export const isDocoonSendOptionsComplete = (v: DocoonSendOptionsValue) => !!v.serviceEdc && !!v.typeDocEdc;
